import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  BarChart3,
  Users,
  Phone,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Clock,
  RefreshCw,
  Activity,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Flame,
  Zap
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminUsageDashboard() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/usage/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const dashboardData = await res.json();
        setData(dashboardData);
        setLastUpdated(new Date());
      }
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "blue" }) => {
    const colorClasses = {
      blue: "bg-blue-100 text-blue-600",
      green: "bg-emerald-100 text-emerald-600",
      purple: "bg-purple-100 text-purple-600",
      amber: "bg-amber-100 text-amber-600",
      red: "bg-red-100 text-red-600",
      slate: "bg-slate-100 text-slate-600"
    };
    
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-500">{title}</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
              {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            </div>
            <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
          </div>
          {trend && (
            <div className="flex items-center gap-1 mt-3">
              {trend === "up" ? (
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-500" />
              )}
              <span className={`text-sm font-medium ${trend === "up" ? "text-emerald-600" : "text-red-600"}`}>
                {trendValue}
              </span>
              <span className="text-sm text-slate-500">vs last period</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Failed to load dashboard data</p>
        <Button onClick={loadDashboardData} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Usage Dashboard</h1>
          <p className="text-slate-500 mt-1">Platform-wide metrics and analytics</p>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdated && (
            <p className="text-sm text-slate-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
          <Button onClick={loadDashboardData} variant="outline" data-testid="refresh-dashboard">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Customers"
          value={data.customers.total}
          subtitle={`${data.customers.active} active`}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${data.revenue.mrr.toLocaleString()}`}
          subtitle="MRR"
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Total Calls"
          value={data.calls.total.toLocaleString()}
          subtitle={`${data.calls.total_minutes.toLocaleString()} minutes`}
          icon={Phone}
          color="purple"
        />
        <StatCard
          title="Total Leads"
          value={data.leads.total.toLocaleString()}
          subtitle={`${data.leads.conversion_rate}% conversion`}
          icon={Target}
          color="amber"
        />
      </div>

      {/* Call Performance & Customer Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Call Performance */}
        <Card data-testid="call-performance-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-slate-600" />
              <CardTitle>Call Performance</CardTitle>
            </div>
            <CardDescription>Call metrics and success rates</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{data.calls.today}</p>
                <p className="text-sm text-slate-500">Today</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{data.calls.this_week}</p>
                <p className="text-sm text-slate-500">This Week</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{data.calls.this_month}</p>
                <p className="text-sm text-slate-500">This Month</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-600">Success Rate</span>
                  <span className="text-sm font-medium">{data.calls.success_rate}%</span>
                </div>
                <Progress value={data.calls.success_rate} className="h-2" />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-600">Completion Rate</span>
                  <span className="text-sm font-medium">{data.calls.completion_rate}%</span>
                </div>
                <Progress value={data.calls.completion_rate} className="h-2" />
              </div>
            </div>
            
            <div className="pt-4 border-t grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-sm">Completed: {data.calls.breakdown.completed}</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm">Failed: {data.calls.breakdown.failed}</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-sm">No Answer: {data.calls.breakdown.no_answer}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-sm">Avg: {Math.round(data.calls.avg_duration_seconds)}s</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer Breakdown */}
        <Card data-testid="customer-breakdown-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-600" />
              <CardTitle>Customer Overview</CardTitle>
            </div>
            <CardDescription>Customer status and growth metrics</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-emerald-700">Active</span>
                </div>
                <p className="text-2xl font-bold text-emerald-900 mt-2">{data.customers.active}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <span className="text-sm text-red-700">Suspended</span>
                </div>
                <p className="text-2xl font-bold text-red-900 mt-2">{data.customers.suspended}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-blue-700">On Trial</span>
                </div>
                <p className="text-2xl font-bold text-blue-900 mt-2">{data.customers.trial}</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <span className="text-sm text-amber-700">Churn Rate</span>
                </div>
                <p className="text-2xl font-bold text-amber-900 mt-2">{data.customers.churn_rate}%</p>
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium text-slate-700 mb-3">New Customers</h4>
              <div className="flex gap-4">
                <div className="flex-1 p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-slate-900">{data.customers.new_this_week}</p>
                  <p className="text-xs text-slate-500">This Week</p>
                </div>
                <div className="flex-1 p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-slate-900">{data.customers.new_this_month}</p>
                  <p className="text-xs text-slate-500">This Month</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leads & Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Pipeline */}
        <Card data-testid="lead-pipeline-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-slate-600" />
              <CardTitle>Lead Pipeline</CardTitle>
            </div>
            <CardDescription>Lead status distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="font-medium text-red-900">Hot Leads</span>
                </div>
                <Badge className="bg-red-500">{data.leads.hot}</Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="font-medium text-amber-900">Warm Leads</span>
                </div>
                <Badge className="bg-amber-500">{data.leads.warm}</Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="font-medium text-blue-900">Qualified</span>
                </div>
                <Badge className="bg-blue-500">{data.leads.qualified}</Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="font-medium text-emerald-900">Converted</span>
                </div>
                <Badge className="bg-emerald-500">{data.leads.converted}</Badge>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t text-center">
              <p className="text-sm text-slate-500">Overall Conversion Rate</p>
              <p className="text-3xl font-bold text-slate-900">{data.leads.conversion_rate}%</p>
            </div>
          </CardContent>
        </Card>

        {/* Revenue & Plans */}
        <Card data-testid="revenue-breakdown-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-slate-600" />
              <CardTitle>Revenue Breakdown</CardTitle>
            </div>
            <CardDescription>MRR by subscription plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-6">
              <p className="text-sm text-slate-500">Monthly Recurring Revenue</p>
              <p className="text-4xl font-bold text-emerald-600">${data.revenue.mrr.toLocaleString()}</p>
            </div>
            
            <div className="space-y-3">
              {Object.entries(data.revenue.plan_distribution).map(([plan, count]) => {
                const planPrices = {
                  free_trial: 0,
                  starter: 49,
                  professional: 99,
                  enterprise: 299
                };
                const revenue = (planPrices[plan] || 0) * count;
                const percentage = data.revenue.mrr > 0 ? (revenue / data.revenue.mrr * 100) : 0;
                
                return (
                  <div key={plan} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium capitalize">{plan.replace(/_/g, " ")}</span>
                      <div className="text-right">
                        <span className="font-bold">${revenue.toLocaleString()}</span>
                        <span className="text-slate-500 text-sm ml-2">({count} customers)</span>
                      </div>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Activity Chart */}
      {data.time_analytics.daily_breakdown.length > 0 && (
        <Card data-testid="daily-activity-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-slate-600" />
              <CardTitle>Daily Activity (Last 7 Days)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between gap-2 h-40">
              {data.time_analytics.daily_breakdown.map((day, idx) => {
                const maxCalls = Math.max(...data.time_analytics.daily_breakdown.map(d => d.calls));
                const heightPercent = maxCalls > 0 ? (day.calls / maxCalls * 100) : 0;
                const date = new Date(day.date);
                const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
                
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex flex-col items-center justify-end h-32">
                      <div
                        className="w-full max-w-[40px] bg-blue-500 rounded-t transition-all"
                        style={{ height: `${heightPercent}%`, minHeight: day.calls > 0 ? "8px" : "0" }}
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-medium text-slate-600">{dayName}</p>
                      <p className="text-xs text-slate-400">{day.calls} calls</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Performing Campaigns */}
      {data.campaigns.top_performing.length > 0 && (
        <Card data-testid="top-campaigns-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-slate-600" />
              <CardTitle>Top Performing Campaigns</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.campaigns.top_performing.slice(0, 5).map((campaign, idx) => (
                <div key={campaign.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-slate-400">#{idx + 1}</span>
                    <div>
                      <p className="font-medium text-slate-900">{campaign.name}</p>
                      <p className="text-sm text-slate-500">
                        {campaign.total_contacted} contacted • {campaign.total_converted} converted
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                    {campaign.conversion_rate?.toFixed(1) || 0}% conv.
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
